import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { URLPaths } from '../../core/mips/common/utilities/urlPaths.config';
import { AuthenticationService } from '../../../app/login/login-page/authentication.service';
import { ConsultantDetailsService } from '../../core/mips/common/services/consultant-details.service';
import { SnackbarService } from '../../core/mips/common/services/snackbar.service';
import { LocalStorageService } from '../../core/mips/common/services/local-storage.service';
import { setUser } from 'app/core/store/currentUser/user.actions';
import { setLoggedInUser } from 'app/core/store/loggedInUser/loggedInUser.action';
import { ConsultantModel } from '../../core/mips/common/models/consultant.model';
import { UserModel } from '../../core/mips/common/models/user.model';
import { Constants } from 'app/core/mips/common/utilities/constants.config';
import { catchError, switchMap, takeUntil, timeout } from 'rxjs/operators';
import { EMPTY, Subject, throwError } from 'rxjs';
import { MsalAuthService } from 'app/login/msal-auth.service';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit, OnDestroy {
  gifImagePath = URLPaths.PATH_USERSEARCH_GIF;
  bigMessage = Constants.PAGES_LANDING_BIG_TEXT;
  smallMessage = Constants.PAGES_LANDING_SMALL_TEXT_REMEMBER;
  API_KEY: string | null = null;
  private readonly destroy$ = new Subject<void>();
  private authorizationStarted = false;

  /*
   * Constructor
   */

  constructor(
    private _authenticationService: AuthenticationService,
    private _router: Router,
    private _consultantDetailsService: ConsultantDetailsService,
    private _snackbarService: SnackbarService,
    private _store: Store,
    private _msalAuthService: MsalAuthService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit() {
    this.beginAuthorisation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setLoggedInUser(user: UserModel) {
    this._store.dispatch(setLoggedInUser({ user }));
  }

  setCurrentUser(user: ConsultantModel) {
    this._store.dispatch(setUser({ user }));
  }

  // Authorisation of the user
  beginAuthorisation() {
    if (this.authorizationStarted) {
      return;
    }
    this.authorizationStarted = true;

    if (this._msalAuthService.isLoggedIn()) {
      void this._router.navigate([URLPaths.ROUTE_TIME_ENTRY_ACTUALS], { replaceUrl: true });
      return;
    }

    this.API_KEY = LocalStorageService.getLocalStorageValueBasedOnKeys(
      'MSAL_JWT_TOKEN'
    );

    if (this.API_KEY) {
      this.smallMessage = Constants.PAGES_LANDING_SMALL_TEXT_FOUND;
      this.validateToken();
    } else {
      void this._router.navigate([URLPaths.ROUTE_LOGIN], { replaceUrl: true });
    }
  }

  // Validate the API Key
  validateToken() {
    this._authenticationService
      .validateToken()
      .pipe(
        switchMap(() => {
          this.smallMessage = Constants.PAGES_LANDING_SMALL_TEXT_VERIFIED;
          this.gifImagePath = URLPaths.PATH_USERFETCHINFO_GIF;
          return this._authenticationService.getUser();
        }),
        switchMap((authenticatedUserDetails) => {
          this.setLoggedInUser(authenticatedUserDetails);
          return this._consultantDetailsService
            .getConsultantDetails()
            .pipe(
              switchMap((consultants: ConsultantModel[]) => {
                const currentUser = consultants.find(
                  (consultant) =>
                    consultant.LoginName.toLowerCase() ===
                    authenticatedUserDetails.username.toLowerCase()
                );

                if (!currentUser) {
                  return throwError(() => new Error('Consultant details not found'));
                }

                this.setCurrentUser(currentUser);
                return this._router.navigate([
                  URLPaths.ROUTE_TIME_ENTRY_ACTUALS,
                ]);
              })
            );
        }),
        timeout(15000),
        catchError((error) => {
          const unauthorized =
            error instanceof HttpErrorResponse && error.status === 401;
          const timedOut = error?.name === 'TimeoutError';

          const message = unauthorized
            ? 'Your session has expired. Please sign in again.'
            : timedOut
              ? 'Authentication timed out. Please try again.'
              : error?.error?.detail || 'Authentication failed. Please try again.';

          this._msalAuthService.clearAuthenticationState();

          this.API_KEY = null;
          void this._router.navigate([URLPaths.ROUTE_LOGIN], {
            replaceUrl: true,
          });
          this._snackbarService.showSnackBar(message, 'close', 'failure');
          return EMPTY;
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }
}
